import { motion } from 'framer-motion';
import { Calendar, MapPin, Mic, ExternalLink } from 'lucide-react';
import { conferencesData } from '../data/conferences';

const ConferencesPage = () => {
  // Sort conferences by year descending (most recent first)
  const sortedConferences = [...conferencesData].sort((a, b) => {
    // Handle year ranges like '2014-2015'
    const getYear = (conf: any) => parseInt((conf.year || '').split('-')[0], 10) || 0;
    return getYear(b) - getYear(a);
  });

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12"
      >
        <h1 className="text-4xl font-bold mb-4">Conferences & Speaking</h1>
        <p className="text-xl text-muted-foreground">
          My speaking engagements and conference presentations across technology and product management.
        </p>
      </motion.div>

      <div className="max-w-6xl mx-auto">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-100 dark:bg-primary-900"></div>

          {sortedConferences.map((conference, index) => (
            <motion.div
              key={conference.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
            >
              {/* Timeline dot */}
              <div className={`absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full border-4 border-background bg-primary-600 transition-all duration-300`}></div>

              {/* Content Card */}
              <motion.div
                className={`w-full md:w-1/2 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}
                whileHover={{ scale: 1.02 }}
              >
                <div className="card cursor-pointer transition-all duration-300 hover:shadow-lg">
                  <div className="card-content">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                      <div className="flex-1 flex items-center gap-3">
                        <div>
                          <h3 className="text-xl font-semibold mt-4 mb-0.5 flex items-center gap-2">
                            <Mic size={18} className="text-primary-600" />
                            {conference.name}
                          </h3>
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <span className="font-medium">{conference.role}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <div className="flex items-center gap-1">
                              <Calendar size={14} />
                              <span>{conference.year}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin size={14} />
                              <span>{conference.location}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-4">
                      {conference.description}
                    </p>

                    {conference.link && (
                      <div className="flex justify-end">
                        <a
                          href={conference.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
                        >
                          <span className="text-sm font-medium">Learn More</span>
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConferencesPage; 